import cn from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import getBoxedValue from 'editor/src/util/getBoxedValue';

import Slider from 'editor/src/component/Slider';

import styles from './index.module.scss';

export const TYPING_COMMIT_DELAY_MS = 400;

interface Props {
  onChange: (value: number) => void;
  onAfterChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  startPoint: number;
  value: number;
  Label: React.FC | React.VFC | string;
  className?: string;
  inputClassName?: string;
  position?: 'singleRow' | 'twoRows';
}

function SliderWithInput({
  onChange,
  onAfterChange,
  className,
  min,
  max,
  step,
  startPoint,
  value,
  Label,
  inputClassName,
  position = 'singleRow',
}: Props) {
  const [inputValue, setInputValue] = useState<string>(value.toString());
  const commitTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => setInputValue(value.toString()), [value]);

  const cancelPendingCommit = () => {
    clearTimeout(commitTimerRef.current);
    commitTimerRef.current = undefined;
  };

  const onSliderChange = useCallback(
    (sliderValue: number) => {
      cancelPendingCommit();
      setInputValue(sliderValue.toString());
      onChange(sliderValue);
    },
    [onChange],
  );

  const toBoxedValue = (text: string) => {
    const parsed = Number(text);
    if (text.trim() === '' || Number.isNaN(parsed)) {
      return undefined;
    }
    const roundedValue = step.toString().includes('.')
      ? parseFloat(parsed.toFixed(step.toString().split('.')[1].length))
      : Math.trunc(parsed);
    return getBoxedValue(roundedValue, min, max);
  };

  // The typed value is shown on the canvas right away (like dragging the slider) and saved once:
  // on Enter, on blur, when the box goes away, or when typing pauses. Typing "40" must not save 4
  // on the way, which would add an extra undo step. Clicking the canvas does not blur the box,
  // so the pause is what saves a value typed without Enter.
  const commitInputValue = (text: string) => {
    cancelPendingCommit();
    const boxedValue = toBoxedValue(text);
    if (boxedValue === undefined) {
      setInputValue(value.toString());
      onChange(value);
      return;
    }

    setInputValue(boxedValue.toString());
    if (boxedValue !== value) {
      onAfterChange(boxedValue);
    }
  };

  const commitRef = useRef<() => void>();
  commitRef.current = () => commitInputValue(inputValue);
  useEffect(
    () => () => {
      if (commitTimerRef.current) {
        commitRef.current?.();
      }
    },
    [],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: targetValue } = e.target;
    if (targetValue !== '' && !/^[+-]?\d*\.?\d*$/.test(targetValue)) {
      return;
    }

    setInputValue(targetValue);
    const boxedValue = toBoxedValue(targetValue);
    if (boxedValue !== undefined) {
      onChange(boxedValue);
    }
    cancelPendingCommit();
    commitTimerRef.current = setTimeout(() => commitRef.current?.(), TYPING_COMMIT_DELAY_MS);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitInputValue(inputValue);
    } else if (e.key === 'Escape') {
      cancelPendingCommit();
      setInputValue(value.toString());
      onChange(value);
    }
  };

  const onInputBlur = () => {
    if (commitTimerRef.current) {
      commitInputValue(inputValue);
    }
  };

  return (
    <div className={cn(styles.mainContainer, styles[position], className)}>
      <div className={styles.label}>{typeof Label === 'string' ? Label : <Label />}</div>
      <Slider
        className={styles.slider}
        min={min}
        max={max}
        step={step}
        startPoint={startPoint}
        value={value}
        onChange={onSliderChange}
        onAfterChange={onAfterChange}
      />
      <input
        className={cn(styles.input, inputClassName)}
        value={inputValue}
        type="text"
        onChange={onInputChange}
        onKeyDown={onInputKeyDown}
        onBlur={onInputBlur}
      />
    </div>
  );
}

export default React.memo(SliderWithInput);
